import styles from "./TimeSlotsSection.module.scss";

import { useCallback, useMemo, useRef } from "react";

import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { AppDropdown } from "@/modules/shared/components/AppDropdown";
import { ArrowIcon } from "@/modules/shared/svgs/ArrowIcon";
import {
  BOOKING_EVENTS,
  RESCHEDULE_EVENTS,
} from "@/modules/tracking/consts/Events";
import { trackBookingEvent } from "@/modules/tracking/utils/trackBookingEvent";

import { BookingKinds, useBookingContext } from "../../context/BookingContext";
import { dateTimeFormats } from "../../conts/dateTimeFormats";
import { useLocalizedTimezoneOptions } from "../../hooks/useLocalizedTimezoneOptions";
import { PlanetIconSvg } from "../../svgs/PlanetIconSvg";
import { buildBookingEventProps } from "../../utils/buildBookingEventProps";
import { TimeSlotSelector } from "../TimeSlotSelector";

type TimeSlotsSectionProps = {
  filteredTimeSpots: string[];
};

export const TimeSlotsSection = ({
  filteredTimeSpots,
}: TimeSlotsSectionProps) => {
  const { t } = useTranslation();
  const {
    selectedDate,
    selectedDateTime,
    timezone,
    setTimezoneOption,
    setSelectedDateTime,
    clearSelectedDate,
    coachId,
    isRescheduling,
    rescheduleSessionId,
    originalSessionStart,
    bookingKind,
    serviceName,
  } = useBookingContext();

  const isCoachMatchingServiceBooking =
    bookingKind === BookingKinds.COACH_MATCHING_SERVICE;

  const eventProps = useMemo(
    () =>
      buildBookingEventProps({
        isRescheduling,
        isCoachMatchingServiceBooking,
        coachId,
        serviceName,
        rescheduleSessionId,
      }),
    [
      isRescheduling,
      isCoachMatchingServiceBooking,
      coachId,
      serviceName,
      rescheduleSessionId,
    ],
  );

  const previousTimeRef = useRef<string | undefined>(selectedDateTime);

  const localizedTimezoneOptions = useLocalizedTimezoneOptions(timezone?.value);

  const formattedDate = useMemo(
    () =>
      formatInTimeZone(
        // eslint-disable-next-line react-hooks/purity
        selectedDate || new Date(),
        timezone?.value || "UTC",
        dateTimeFormats.selectedDate,
      ),
    [selectedDate, timezone?.value],
  );

  const handleTimeSlotSelected = useCallback(
    (dateTime: string | undefined) => {
      if (dateTime && previousTimeRef.current) {
        const changedEvent = isRescheduling
          ? RESCHEDULE_EVENTS.TIME_SLOT_CHANGED
          : BOOKING_EVENTS.TIME_SLOT_CHANGED;
        trackBookingEvent(changedEvent, {
          ...eventProps,
          previous_time: previousTimeRef.current,
          new_time: dateTime,
        });
      } else if (dateTime) {
        const selectedEvent = isRescheduling
          ? RESCHEDULE_EVENTS.TIME_SLOT_SELECTED
          : BOOKING_EVENTS.TIME_SLOT_SELECTED;
        trackBookingEvent(selectedEvent, {
          ...eventProps,
          ...(isRescheduling
            ? { original_session_start_time: originalSessionStart ?? null }
            : {}),
          session_start_time: dateTime,
        });
      }

      previousTimeRef.current = dateTime;
      setSelectedDateTime(dateTime);
    },
    [eventProps, setSelectedDateTime, isRescheduling, originalSessionStart],
  );

  return (
    <div className={styles.timeSectionContainer}>
      <div className={styles.timeSlotsHeader}>
        <div className={styles.arrowIconContainer} onClick={clearSelectedDate}>
          <ArrowIcon />
        </div>
        <div className={styles.headerContent}>
          <span className={styles.title}>{t("bookingWidget.selectTime")}</span>
          <span className={styles.dateSubtitle}>{formattedDate}</span>
          <div className={styles.timezoneRow}>
            <PlanetIconSvg />
            <AppDropdown
              selectedOption={timezone}
              options={localizedTimezoneOptions}
              onSelect={setTimezoneOption}
              testId="select-time-timezone-select"
            />
          </div>
        </div>
      </div>
      <div className={styles.timeSlotSelectorWrapper}>
        <TimeSlotSelector
          onSelected={handleTimeSlotSelected}
          slots={filteredTimeSpots}
          timezone={timezone?.value}
        />
      </div>
    </div>
  );
};
